// public/js/features/distribution/order-modal.js
// Two superadmin modals for device orders (docs/device-distribution.md "Orders").
//
//   openCreateOrderModal(tenant, openRequests, onChanged)
//     Build an order FROM a tenant's open requests: pick lines and quantities (up
//     to what is still open), choose or add a shipping address, set invoice /
//     currency / incoterm / sale-or-sample and unit prices. Creating it draws
//     the quantities down on the requests.
//
//   openOrderDetailModal(order, onChanged)
//     Move an order through ordered -> preparing -> shipped -> delivered, record
//     the DHL tracking number (shipping needs one), scan the units that go into
//     the box (serial + SIM), note the packages, and pull the customs data sheet.
//     When `shopOrders` is on, it also shows the shopofthings.ch order the push
//     created, and lets a failed push be retried (docs/shop-orders.md).
import { listAddresses, createAddress, createOrder, updateOrder, datenblattUrl } from './api.js';
import { featureEnabled } from '../../modules/features.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(navigator.language, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso || ''; } };
const toast = (msg, kind = 'success') => window.showToast?.(msg, kind);

export const STATUS_LABEL = { ordered: 'Ordered', preparing: 'Preparing', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled' };
export const STATUS_TONE  = { ordered: 'blue', preparing: 'amber', shipped: 'blue', delivered: 'green', cancelled: 'gray' };
export const statusPill = (s) => `<span class="badge badge-soft-${STATUS_TONE[s] || 'gray'}">${esc(STATUS_LABEL[s] || s)}</span>`;

const TAX_ID_TYPES = ['', 'USt-IdNr.', 'VAT', 'UID', 'EIN', 'RFC', 'RUC', 'CNPJ', 'GSTIN', 'ABN', 'other'];
const INCOTERMS = ['DAP', 'DDP'];
const CURRENCIES = ['EUR', 'CHF', 'USD', 'GBP'];

function mountModal(id, html) {
  document.getElementById(id)?.remove();
  document.body.insertAdjacentHTML('beforeend', html);
  const el = document.getElementById(id);
  const modal = new bootstrap.Modal(el);
  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });
  return { el, modal };
}

// Address book entry -> the picker's one-line summary. The label is the human
// name; the rest is there so two "Hamburg" entries can still be told apart.
const addressSummary = (a) => [a.label, a.name, [a.zip, a.city, a.state].filter(Boolean).join(' '), a.country, a.contact]
  .filter(Boolean).join(' · ');

const addressFormHtml = (a = {}) => `
  <div class="row g-2">
    <div class="col-12 col-md-6"><label class="form-label small mb-0">Label <span class="text-muted">(what the picker shows)</span></label><input class="form-control form-control-sm" data-f="label" value="${esc(a.label || '')}" placeholder="e.g. Hamburg hangar, Ms Weber"></div>
    <div class="col-12 col-md-6"><label class="form-label small mb-0">Company / consignee *</label><input class="form-control form-control-sm" data-f="name" value="${esc(a.name || '')}"></div>
    <div class="col-12 col-md-6"><label class="form-label small mb-0">Contact person</label><input class="form-control form-control-sm" data-f="contact" value="${esc(a.contact || '')}"></div>
    <div class="col-12 col-md-6"><label class="form-label small mb-0">Street * <span class="text-muted">(one line per address line)</span></label><textarea class="form-control form-control-sm" data-f="street" rows="2" placeholder="Building / hangar&#10;Street and number">${esc(a.street || '')}</textarea></div>
    <div class="col-4 col-md-2"><label class="form-label small mb-0">ZIP</label><input class="form-control form-control-sm" data-f="zip" value="${esc(a.zip || '')}"></div>
    <div class="col-8 col-md-4"><label class="form-label small mb-0">City *</label><input class="form-control form-control-sm" data-f="city" value="${esc(a.city || '')}"></div>
    <div class="col-6 col-md-3"><label class="form-label small mb-0">State / province <span class="text-muted">(US, CA, MX…)</span></label><input class="form-control form-control-sm" data-f="state" value="${esc(a.state || '')}" placeholder="IL"></div>
    <div class="col-6 col-md-3"><label class="form-label small mb-0">Country * <span class="text-muted">(ISO-2)</span></label><input class="form-control form-control-sm text-uppercase" data-f="country" maxlength="2" value="${esc(a.country || '')}" placeholder="DE"></div>
    <div class="col-12 col-md-6"><label class="form-label small mb-0">Incoterm</label><select class="form-select form-select-sm" data-f="incoterm">${INCOTERMS.map(t => `<option ${a.incoterm === t ? 'selected' : ''}>${t}</option>`).join('')}</select></div>
    <div class="col-12 col-md-6"><label class="form-label small mb-0">Email</label><input class="form-control form-control-sm" data-f="email" type="email" value="${esc(a.email || '')}"></div>
    <div class="col-12 col-md-6"><label class="form-label small mb-0">Phone</label><input class="form-control form-control-sm" data-f="phone" value="${esc(a.phone || '')}"></div>
    <div class="col-4 col-md-3"><label class="form-label small mb-0">Tax ID type</label><select class="form-select form-select-sm" data-f="taxIdType">${TAX_ID_TYPES.map(t => `<option value="${t}" ${a.taxIdType === t ? 'selected' : ''}>${t || '—'}</option>`).join('')}</select></div>
    <div class="col-8 col-md-4"><label class="form-label small mb-0">Tax ID</label><input class="form-control form-control-sm" data-f="taxId" value="${esc(a.taxId || '')}"></div>
    <div class="col-12 col-md-5"><label class="form-label small mb-0">EORI <span class="text-muted">(EU)</span></label><input class="form-control form-control-sm" data-f="eori" value="${esc(a.eori || '')}"></div>
    <div class="col-12"><label class="form-label small mb-0">Notes</label><input class="form-control form-control-sm" data-f="notes" value="${esc(a.notes || '')}"></div>
  </div>`;

const readAddressForm = (root) => {
  const a = {};
  root.querySelectorAll('[data-f]').forEach(i => { a[i.dataset.f] = i.value.trim() || null; });
  return a;
};

// ── Create order ─────────────────────────────────────────────────────────────
export async function openCreateOrderModal(tenant, openRequests, onChanged) {
  const id = 'order-create-modal';
  const reqs = (openRequests || []).filter(r => r.tenantId === tenant.tenantId && r.status !== 'completed');
  // One row per open request line with something left to order.
  const lines = [];
  reqs.forEach(r => (r.items || []).forEach(it => {
    const remaining = Math.max(0, (it.qty || 0) - (it.ordered || 0));
    if (remaining > 0) lines.push({ requestId: r.requestId, model: it.model, remaining, priority: r.priority, requestedAt: r.requestedAt });
  }));

  const lineRows = lines.map((l, i) => `
    <tr>
      <td><input type="checkbox" class="form-check-input ol-pick" data-i="${i}" id="ol-pick-${i}" checked></td>
      <td><label for="ol-pick-${i}" class="mb-0 fw-semibold">${esc(l.model)}</label><div class="small text-muted">request P${l.priority} · ${l.remaining} still open</div></td>
      <td style="width:110px;"><input type="number" class="form-control form-control-sm ol-qty" data-i="${i}" min="1" max="${l.remaining}" value="${l.remaining}"></td>
      <td style="width:130px;"><input type="number" class="form-control form-control-sm ol-price" data-i="${i}" min="0" step="0.01" value="0" title="Unit price / declared customs value"></td>
      <td style="width:110px;"><input class="form-control form-control-sm ol-sku" data-i="${i}" value="${esc(l.model)}" title="SKU for the warehouse export. Leave it as the model name: the shop SKU is resolved from SHOP_SKU_MAP when the order is pushed. Type a shop SKU here to override it."></td>
    </tr>`).join('');

  const { el, modal } = mountModal(id, `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fa-solid fa-cart-plus me-2"></i>New order — ${esc(tenant.name)}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            ${lines.length ? `
            <h6 class="small text-uppercase text-muted fw-semibold">1 · What to ship <span class="fw-normal text-lowercase">— from the open requests, not everything has to go</span></h6>
            <div class="table-responsive mb-3">
              <table class="table table-sm align-middle mb-0">
                <thead class="table-light"><tr><th style="width:36px;"></th><th>Model</th><th>Qty</th><th>Unit price</th><th>SKU</th></tr></thead>
                <tbody>${lineRows}</tbody>
              </table>
            </div>` : `<div class="alert alert-warning">This tenant has no open request lines left to order from.</div>`}

            <h6 class="small text-uppercase text-muted fw-semibold">2 · Ship to</h6>
            <div class="d-flex flex-wrap gap-2 align-items-end mb-2">
              <div class="flex-grow-1" style="min-width:260px;">
                <label class="form-label small mb-0">Saved address</label>
                <select class="form-select form-select-sm" id="oa-pick"><option value="">Loading…</option></select>
              </div>
              <div class="form-check mb-1">
                <input class="form-check-input" type="checkbox" id="oa-saveNew">
                <label class="form-check-label small" for="oa-saveNew">Save as a new address book entry</label>
              </div>
            </div>
            <div id="oa-form" class="border rounded p-2 mb-3">${addressFormHtml({})}</div>

            <h6 class="small text-uppercase text-muted fw-semibold">3 · Commercial</h6>
            <div class="row g-2 mb-2">
              <div class="col-6 col-md-3"><label class="form-label small mb-0">Invoice number</label><input class="form-control form-control-sm" id="oc-invoice" placeholder="from accounting"></div>
              <div class="col-6 col-md-2"><label class="form-label small mb-0">Currency</label><select class="form-select form-select-sm" id="oc-currency">${CURRENCIES.map(c => `<option>${c}</option>`).join('')}</select></div>
              <div class="col-6 col-md-2"><label class="form-label small mb-0">Incoterm</label><select class="form-select form-select-sm" id="oc-incoterm">${INCOTERMS.map(t => `<option>${t}</option>`).join('')}</select></div>
              <div class="col-6 col-md-5"><label class="form-label small mb-0">Delivery type</label>
                <select class="form-select form-select-sm" id="oc-pricing">
                  <option value="sale">Sale — paid delivery</option>
                  <option value="sample">Free test devices — declared customs value only</option>
                </select></div>
              <div class="col-12"><label class="form-label small mb-0">Internal note</label><input class="form-control form-control-sm" id="oc-note"></div>
            </div>
            <div id="oc-error" class="text-danger small" hidden></div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" id="oc-create" ${lines.length ? '' : 'disabled'}><i class="fa-solid fa-cart-plus me-1"></i>Create order</button>
          </div>
        </div>
      </div>
    </div>`);

  // Address book: pick fills the form; editing the form + "save as new" adds a
  // fresh entry rather than silently changing the one other orders point at.
  let addresses = [];
  const pick = el.querySelector('#oa-pick'), form = el.querySelector('#oa-form');
  const fillForm = (a) => { form.innerHTML = addressFormHtml(a || {}); };
  try {
    addresses = await listAddresses(tenant.tenantId);
    pick.innerHTML = `<option value="">— New address —</option>` + addresses.map(a => `<option value="${esc(a.addressId)}">${esc(addressSummary(a))}</option>`).join('');
    if (addresses.length) { pick.value = addresses[0].addressId; fillForm(addresses[0]); el.querySelector('#oc-incoterm').value = addresses[0].incoterm || 'DAP'; }
  } catch { pick.innerHTML = `<option value="">— New address —</option>`; }
  pick.addEventListener('change', () => {
    const a = addresses.find(x => x.addressId === pick.value);
    fillForm(a);
    if (a?.incoterm) el.querySelector('#oc-incoterm').value = a.incoterm;
    el.querySelector('#oa-saveNew').checked = !a;   // a brand-new address is worth keeping by default
  });
  form.addEventListener('input', () => { if (pick.value) el.querySelector('#oa-saveNew').checked = true; });   // edited a book entry -> offer to keep it as new

  el.querySelector('#oc-create')?.addEventListener('click', async () => {
    const err = el.querySelector('#oc-error'); err.hidden = true;
    const picked = [...el.querySelectorAll('.ol-pick')].filter(c => c.checked).map(c => Number(c.dataset.i));
    const orderLines = picked.map(i => ({
      requestId: lines[i].requestId, model: lines[i].model,
      qty: Math.min(lines[i].remaining, Math.max(1, Math.round(+el.querySelector(`.ol-qty[data-i="${i}"]`).value || 0))),
      unitPrice: +el.querySelector(`.ol-price[data-i="${i}"]`).value || 0,
      sku: el.querySelector(`.ol-sku[data-i="${i}"]`).value.trim() || lines[i].model,
    })).filter(l => l.qty > 0);
    if (!orderLines.length) { err.textContent = 'Pick at least one line.'; err.hidden = false; return; }
    const address = readAddressForm(form);
    if (!address.name || !address.street || !address.city || !address.country) { err.textContent = 'The address needs company, street, city and country.'; err.hidden = false; return; }
    const saveNew = el.querySelector('#oa-saveNew').checked;
    const payload = {
      tenantId: tenant.tenantId, lines: orderLines,
      addressId: !saveNew && pick.value ? pick.value : null,
      address, saveAddressAsNew: saveNew || !pick.value,
      invoiceNumber: el.querySelector('#oc-invoice').value.trim() || null,
      currency: el.querySelector('#oc-currency').value, incoterm: el.querySelector('#oc-incoterm').value,
      pricing: el.querySelector('#oc-pricing').value, note: el.querySelector('#oc-note').value.trim() || null,
    };
    const btn = el.querySelector('#oc-create'); btn.disabled = true;
    try {
      const { order } = await createOrder(payload);
      toast(`Order ${order.orderNo} created`);
      modal.hide(); onChanged?.(order);
    } catch (e) { err.textContent = e.message; err.hidden = false; btn.disabled = false; }
  });

  modal.show();
}

// ── Order detail ─────────────────────────────────────────────────────────────
// The shopofthings.ch half. Only rendered where the environment can push at all
// - on an environment without the flag the whole thing is noise, because no
// order will ever carry a `shop` stamp.
function shopBlock(o) {
  if (!featureEnabled('shopOrders')) return '';
  const sh = o.shop || null;
  const sendBtn = o.status === 'cancelled' ? ''
    : '<button class="btn btn-sm btn-outline-primary" id="od-shop-send"><i class="fa-solid fa-paper-plane me-1"></i>Send to shop</button>';
  let body;
  if (sh?.orderId) {
    body = `<div class="d-flex align-items-center gap-2 flex-wrap">
         <span class="badge badge-soft-green">Sent</span>
         <a href="${esc(sh.adminUrl || '#')}" target="_blank" rel="noopener" class="small">
           <i class="fa-solid fa-cart-shopping me-1"></i>Shop order ${esc(sh.orderNo || sh.orderId)}</a>
         ${sh.status ? `<span class="text-muted small">${esc(sh.status)}</span>` : ''}
         ${sh.syncedAt ? `<span class="text-muted small">${fmtDate(sh.syncedAt)}</span>` : ''}
       </div>`;
  } else if (sh?.queuedAt && !sh.error) {
    // Queued, not done. The push runs in a background invoke because the shop's
    // own order hooks can take a while, so there is nothing to wait for here -
    // the panel says so instead of spinning forever.
    body = `<div class="d-flex align-items-center gap-2 flex-wrap">
         <span class="badge badge-soft-blue"><i class="fa-solid fa-paper-plane me-1"></i>Sending</span>
         <span class="text-muted small">queued ${fmtDate(sh.queuedAt)}</span>
         <button class="btn btn-sm btn-outline-secondary" id="od-shop-refresh"><i class="fa-solid fa-rotate me-1"></i>Check</button>
       </div>
       <div class="small text-muted mt-1">The shop takes a moment to accept an order. Check again in a few seconds.</div>`;
  } else {
    body = `<div class="d-flex align-items-center gap-2 flex-wrap">
         <span class="badge badge-soft-${sh?.error ? 'red' : 'amber'}">${sh?.error ? 'Failed' : 'Not sent'}</span>
         ${sendBtn}
       </div>
       ${sh?.error ? `<div class="small text-danger mt-1">${esc(sh.error)}</div>` : ''}`;
  }
  return `<div class="small text-muted text-uppercase fw-semibold mt-3 mb-1">Shop</div>${body}`;
}

export function openOrderDetailModal(order, onChanged) {
  const id = 'order-detail-modal';
  let o = order;
  let dirty = false;

  const render = () => {
    const a = o.address || {};
    const next = { ordered: 'preparing', preparing: 'shipped', shipped: 'delivered' }[o.status];
    const nextLabel = { preparing: 'Start preparing', shipped: 'Mark shipped', delivered: 'Mark delivered' }[next];
    const trackingUrl = o.tracking?.url;
    const canScan = ['ordered', 'preparing'].includes(o.status);
    return `
      <div class="modal-header">
        <h5 class="modal-title"><i class="fa-solid fa-box me-2"></i>${esc(o.orderNo)} <span class="ms-2">${statusPill(o.status)}</span></h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <div class="row g-3">
          <div class="col-12 col-lg-7">
            <div class="small text-muted text-uppercase fw-semibold mb-1">Lines</div>
            <table class="table table-sm mb-3"><tbody>
              ${(o.lines || []).map(l => `<tr><td class="fw-semibold">${l.qty} × ${esc(l.model)}</td><td class="text-muted small">${esc(l.sku || '')}</td><td class="text-end small">${(l.unitPrice || 0).toFixed(2)} ${esc(o.currency)}</td></tr>`).join('')}
            </tbody></table>

            <div class="small text-muted text-uppercase fw-semibold mb-1">Units in the box <span class="fw-normal text-lowercase">— scan serial + SIM (bench step 14)</span></div>
            <table class="table table-sm mb-1" id="od-devices"><tbody>
              ${(o.devices || []).map((d, i) => `<tr>
                <td class="font-monospace small">${esc(d.serial || d.deviceId)}${d.serial && d.deviceId && d.deviceId !== d.serial ? `<span class="d-block text-muted" title="IMEI">${esc(d.deviceId)}</span>` : ''}</td>
                <td class="font-monospace small text-muted">${esc(d.sim || '')}</td>
                <td class="small">${d.granted ? '<span class="badge badge-soft-green">Granted</span>' : d.deviceId ? '<span class="badge badge-soft-blue" title="Known device; granted to the tenant on shipping">Known</span>' : '<span class="badge badge-soft-amber" title="No uplink from this unit yet - it is granted once it has reported for the first time">Not seen yet</span>'}</td>
                <td class="text-end">${canScan ? `<button class="btn btn-link btn-sm text-danger p-0 od-dev-rm" data-i="${i}" title="Remove"><i class="fa-solid fa-xmark"></i></button>` : ''}</td></tr>`).join('') || `<tr><td class="text-muted small" colspan="4">Nothing scanned yet.</td></tr>`}
            </tbody></table>
            ${!canScan && o.status !== 'cancelled' && (o.devices || []).some(d => !d.granted) ? `<div class="mb-2"><button class="btn btn-sm btn-outline-primary" id="od-retry-grants" title="Resolve units that had not reported yet and grant them to the tenant"><i class="fa-solid fa-rotate me-1"></i>Retry grants</button></div>` : ''}
            ${canScan ? `
            <div class="d-flex gap-2 mb-3">
              <input class="form-control form-control-sm font-monospace" id="od-serial" placeholder="Serial / IMEI" autocomplete="off">
              <input class="form-control form-control-sm font-monospace" id="od-sim" placeholder="SIM / ICCID" autocomplete="off">
              <button class="btn btn-sm btn-outline-primary text-nowrap" id="od-dev-add"><i class="fa-solid fa-barcode me-1"></i>Add</button>
            </div>` : `<div class="small text-muted mb-3">${(o.devices || []).length} unit(s) ${o.status === 'cancelled' ? '' : `shipped, ${(o.devices || []).filter(d => d.granted).length} granted to the tenant`}.</div>`}

            <div class="small text-muted text-uppercase fw-semibold mb-1">Packages</div>
            <table class="table table-sm mb-1"><tbody>
              ${(o.packages || []).map((p, i) => `<tr><td>${p.grossKg} kg</td><td class="text-muted small">${[p.lengthCm, p.widthCm, p.heightCm].filter(Boolean).join(' × ') || ''}${p.lengthCm ? ' cm' : ''}</td><td class="text-end">${canScan ? `<button class="btn btn-link btn-sm text-danger p-0 od-pkg-rm" data-i="${i}"><i class="fa-solid fa-xmark"></i></button>` : ''}</td></tr>`).join('') || `<tr><td class="text-muted small" colspan="3">No packages recorded.</td></tr>`}
            </tbody></table>
            ${canScan ? `
            <div class="d-flex gap-2 mb-3">
              <input type="number" step="0.01" min="0" class="form-control form-control-sm" id="od-kg" placeholder="gross kg" style="max-width:110px;">
              <input type="number" class="form-control form-control-sm" id="od-l" placeholder="L cm" style="max-width:90px;">
              <input type="number" class="form-control form-control-sm" id="od-w" placeholder="W cm" style="max-width:90px;">
              <input type="number" class="form-control form-control-sm" id="od-h" placeholder="H cm" style="max-width:90px;">
              <button class="btn btn-sm btn-outline-primary text-nowrap" id="od-pkg-add"><i class="fa-solid fa-plus me-1"></i>Add</button>
            </div>` : ''}
          </div>

          <div class="col-12 col-lg-5">
            <div class="small text-muted text-uppercase fw-semibold mb-1">Ship to</div>
            <div class="border rounded p-2 small mb-3">
              <div class="fw-semibold">${esc(a.name || '—')}</div>
              ${a.contact ? `<div>${esc(a.contact)}</div>` : ''}
              <div style="white-space:pre-line">${esc(a.street || '')}</div>
              <div>${esc([a.zip, a.city].filter(Boolean).join(' '))}${a.state ? ', ' + esc(a.state) : ''} ${esc(a.country || '')}</div>
              ${a.email ? `<div class="text-muted">${esc(a.email)}${a.phone ? ` · ${esc(a.phone)}` : ''}</div>` : ''}
              ${a.taxId ? `<div class="text-muted">${esc(a.taxIdType || 'Tax ID')} ${esc(a.taxId)}</div>` : ''}
              ${a.eori ? `<div class="text-muted">EORI ${esc(a.eori)}</div>` : ''}
              <div class="mt-1"><span class="badge badge-soft-gray">${esc(o.incoterm)}</span> <span class="badge badge-soft-gray">${o.pricing === 'sample' ? 'Free test devices' : 'Sale'}</span> ${o.invoiceNumber ? `<span class="badge badge-soft-gray">Inv. ${esc(o.invoiceNumber)}</span>` : ''}</div>
            </div>

            <div class="small text-muted text-uppercase fw-semibold mb-1">Tracking</div>
            <div class="d-flex gap-2 mb-1">
              <select class="form-select form-select-sm" id="od-carrier" style="max-width:110px;"><option ${o.tracking?.carrier === 'DHL' || !o.tracking ? 'selected' : ''}>DHL</option><option ${o.tracking?.carrier && o.tracking.carrier !== 'DHL' ? 'selected' : ''}>Other</option></select>
              <input class="form-control form-control-sm font-monospace" id="od-tracking" placeholder="Tracking number" value="${esc(o.tracking?.number || '')}">
              <button class="btn btn-sm btn-outline-secondary text-nowrap" id="od-tracking-save">Save</button>
            </div>
            ${trackingUrl ? `<a href="${esc(trackingUrl)}" target="_blank" rel="noopener" class="small"><i class="fa-solid fa-truck-fast me-1"></i>Open ${esc(o.tracking.carrier)} tracking</a>` : `<div class="small text-muted">Shipping needs a tracking number.</div>`}

            ${shopBlock(o)}

            <div class="small text-muted text-uppercase fw-semibold mt-3 mb-1">History</div>
            <ul class="list-unstyled small mb-0">${(o.history || []).map(h => `<li>${statusPill(h.status)} <span class="text-muted">${fmtDate(h.at)}</span></li>`).join('')}</ul>
          </div>
        </div>
        <div id="od-error" class="text-danger small mt-2" hidden></div>
      </div>
      <div class="modal-footer justify-content-between">
        <div class="d-flex gap-2">
          <a class="btn btn-sm btn-outline-secondary od-sheet" href="#"><i class="fa-solid fa-file-export me-1"></i>Customs data sheet</a>
          ${o.status !== 'cancelled' && o.status !== 'delivered' ? `<button class="btn btn-sm btn-outline-danger" id="od-cancel">Cancel order</button>` : ''}
        </div>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">Close</button>
          ${next ? `<button class="btn btn-primary btn-sm" id="od-next"><i class="fa-solid fa-arrow-right me-1"></i>${nextLabel}</button>` : ''}
        </div>
      </div>`;
  };

  const { el, modal } = mountModal(id, `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-scrollable"><div class="modal-content">${render()}</div></div>
    </div>`);
  el.addEventListener('hidden.bs.modal', () => { if (dirty) onChanged?.(); }, { once: true });

  const apply = async (patch, okMsg) => {
    const err = el.querySelector('#od-error'); err.hidden = true;
    try {
      const res = await updateOrder(o.orderId, patch);
      o = res.order; dirty = true;
      if (res.granted?.length) {
        const ok = res.granted.filter(g => g.ok).length;
        const pending = res.granted.filter(g => g.reason === 'not_seen_yet').length;
        const failed = res.granted.length - ok - pending;
        const parts = [`${ok} unit(s) granted to the tenant`];
        if (pending) parts.push(`${pending} not seen yet - retry after their first uplink`);
        if (failed) parts.push(`${failed} failed`);
        toast(parts.join(', '), failed ? 'warning' : pending ? 'info' : 'success');
      }
      if (okMsg) toast(okMsg);
      el.querySelector('.modal-content').innerHTML = render(); wire();
    } catch (e) {
      // A call can fail and still have saved something - the shop push records
      // its own failure on the order. Take the record back if we got one, so
      // the panel shows the error next to a re-enabled retry button.
      if (e.body?.order) { o = e.body.order; dirty = true; el.querySelector('.modal-content').innerHTML = render(); wire(); }
      const er = el.querySelector('#od-error'); er.textContent = e.message; er.hidden = false;
    }
  };

  const wire = () => {
    // The data sheet is a download behind auth: fetch with the token, hand the
    // bytes to the browser as a file.
    el.querySelector('.od-sheet')?.addEventListener('click', async (ev) => {
      ev.preventDefault();
      try {
        const res = await window.authenticatedFetch(datenblattUrl(o.orderId));
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `datenblatt_${o.orderNo}.json`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      } catch (e) { toast('Could not fetch the data sheet', 'danger'); }
    });
    el.querySelector('#od-dev-add')?.addEventListener('click', () => {
      const serial = el.querySelector('#od-serial').value.trim(), sim = el.querySelector('#od-sim').value.trim();
      if (!serial) return;
      if ((o.devices || []).some(d => d.deviceId === serial || d.serial === serial)) { toast('Already in the box', 'warning'); return; }
      apply({ devices: [...(o.devices || []), { serial, sim: sim || null }] });
    });
    // Scanners send Enter: serial field jumps to SIM, SIM field adds.
    el.querySelector('#od-serial')?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); el.querySelector('#od-sim').focus(); } });
    el.querySelector('#od-sim')?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); el.querySelector('#od-dev-add').click(); } });
    el.querySelectorAll('.od-dev-rm').forEach(b => b.addEventListener('click', () => apply({ devices: (o.devices || []).filter((_, i) => i !== Number(b.dataset.i)) })));
    el.querySelector('#od-pkg-add')?.addEventListener('click', () => {
      const kg = +el.querySelector('#od-kg').value; if (!(kg > 0)) return;
      apply({ packages: [...(o.packages || []), { grossKg: kg, lengthCm: +el.querySelector('#od-l').value || null, widthCm: +el.querySelector('#od-w').value || null, heightCm: +el.querySelector('#od-h').value || null }] });
    });
    el.querySelectorAll('.od-pkg-rm').forEach(b => b.addEventListener('click', () => apply({ packages: (o.packages || []).filter((_, i) => i !== Number(b.dataset.i)) })));
    el.querySelector('#od-tracking-save')?.addEventListener('click', () => {
      const number = el.querySelector('#od-tracking').value.trim();
      const carrier = el.querySelector('#od-carrier').value;
      apply({ tracking: number ? { carrier, number } : null }, number ? 'Tracking saved' : 'Tracking cleared');
    });
    el.querySelector('#od-next')?.addEventListener('click', () => {
      const next = { ordered: 'preparing', preparing: 'shipped', shipped: 'delivered' }[o.status];
      const patch = { status: next };
      if (next === 'shipped') {
        const number = el.querySelector('#od-tracking').value.trim();
        if (!number && !o.tracking?.number) { const er = el.querySelector('#od-error'); er.textContent = 'Enter the tracking number first.'; er.hidden = false; return; }
        if (number) patch.tracking = { carrier: el.querySelector('#od-carrier').value, number };
      }
      apply(patch, `Order ${STATUS_LABEL[next].toLowerCase()}`);
    });
    el.querySelector('#od-cancel')?.addEventListener('click', () => apply({ status: 'cancelled' }, 'Order cancelled'));
    el.querySelector('#od-retry-grants')?.addEventListener('click', () => apply({ retryGrants: true }));
    el.querySelector('#od-shop-send')?.addEventListener('click', (ev) => {
      // One push per click. The backend only queues it, so this returns at once
      // and the panel switches to "Sending"; it is idempotent on the order
      // number anyway, so a second click cannot create a second shop order.
      ev.currentTarget.disabled = true;
      apply({ sendToShop: true }, 'Queued for the shop');
    });
    el.querySelector('#od-shop-refresh')?.addEventListener('click', async (ev) => {
      ev.currentTarget.disabled = true;
      try {
        const res = await window.authenticatedFetch(`api/v1/admin/distribution/orders/${encodeURIComponent(o.orderId)}`);
        o = (await res.json()).order; dirty = true;
        el.querySelector('.modal-content').innerHTML = render(); wire();
      } catch (e) { toast('Could not refresh', 'warning'); }
    });
  };
  wire();
  modal.show();
}
